import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { FormState } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form'
import ReactHotkeys from 'react-hot-keys';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Modal } from 'semantic-ui-react';
import { searchPatients } from 'services/patientServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';

import { PatientDto, PatientSearchRequest } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { isKeyCheck } from 'utils/keyUtils';
import { isValueEmpty } from 'utils/validatorUtils';

interface PatientSearchValues extends FormState {
  values: Partial<PatientSearchRequest>;
}

const TableWrapperDiv = styled.div`
  height: 400px;
`;

const rowStyle = {
  cursor: "pointer",
  userSelect: "none"
};

const selectedRow = {
  ...rowStyle,
  backgroundColor: "#e0e0e0"
};

const StyledGrid = styled(Grid.Column)`
  padding-top: 0.65rem;
`;

const CheckboxDiv = styled.div`
  display: flex;
  flex-direction: row;

  .checkbox {
    margin-right: 1.5rem;
  }
`

interface Props extends MessageSourceProps {
  patientSearchOpen: boolean,
  initialPatients?: Array<PatientDto>,
  toggleModal: () => void,
  onPatientSelected: (patient?: PatientDto) => void,
}

interface State {
  foundPatients: Array<PatientDto>,
  selectedPatientIx?: number,
  selectedPatient?: PatientDto,
  patientsLoaded: boolean
}

class PatientSearch extends Component<Props, State> {

  private cancelTokenSource?: CancelTokenSource = undefined;

  constructor(props: Props) {
    super(props);

    const {initialPatients} = this.props;


    this.state = {
      foundPatients: initialPatients || [],
      selectedPatientIx: undefined,
      selectedPatient: undefined,
      patientsLoaded: true
    };
  }

  componentWillUnmount() {
    this.cancelTokenSource && this.cancelTokenSource.cancel("component_unmount");
  }

  resetSearchData = () => {
    this.setState({
      foundPatients: [],
      selectedPatient: undefined
    });
  };

  onEnterPressed = (e: any) => {

    const {selectedPatientIx} = this.state;

    if (e.key == "Enter") {
      const {foundPatients} = this.state;

      if (foundPatients.length > 0 && selectedPatientIx !== undefined) {
        this.onPatientChosen(foundPatients[selectedPatientIx]);
      }
    }
  };

  onPatientChosen = (selectedPatient: PatientDto | undefined) => {

    const {onPatientSelected} = this.props;

    onPatientSelected(selectedPatient);
    this.resetSearchData();
  };

  onCloseModal = () => {

    const {toggleModal} = this.props;

    toggleModal();
    this.resetSearchData();
  };

  markPatient = (patientIndex: number) => {

    const {foundPatients} = this.state;
    this.setState({
      selectedPatient: foundPatients[patientIndex]
    });
  };

  searchPatient = debounce(({values}: Partial<PatientSearchValues>) => {

    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel("stale_search");
    }
    this.cancelTokenSource = axios.CancelToken.source();

    const onFinally = () => {
      this.setState({
        patientsLoaded: true
      })
    };

    if (values && (!isValueEmpty(values.searchText) || !isValueEmpty(values.firstNameSearchText))) {
      this.setState({patientsLoaded: false});

      searchPatients(this.cancelTokenSource, values).then((patients) => {
            this.setState({
              foundPatients: patients,
              selectedPatientIx: patients.length > 0 ? 0 : undefined,
              selectedPatient: patients.length > 0 ? patients[0] : undefined
            });
          })
          .catch(noop)
          .finally(onFinally);
    }
  }, 375);

  onSelectPatientRow(foundPatient: any, index: any) {
    this.setState({
      selectedPatient: foundPatient,
      selectedPatientIx: index
    });
  }

  changeSelectedPatient(event: any, source?: string) {
    event.persist();
    const {selectedPatientIx, foundPatients} = this.state;

    const isKey: any = isKeyCheck(event);

    if (selectedPatientIx !== undefined) {

      const goDown = foundPatients.length > 0 && (selectedPatientIx + 1 <= foundPatients.length - 1);
      const goUp = foundPatients.length > 0 && (selectedPatientIx - 1 >= 0);

      const selectPatient = selectedPatientIx >= 0 && selectedPatientIx <= foundPatients.length - 1;

      if (goDown && isKey.arrowDown) {
        const index = selectedPatientIx + 1;
        this.onSelectPatientRow(foundPatients[index], index);
      } else if (goUp && isKey.arrowUp) {
        const index = selectedPatientIx - 1;
        this.onSelectPatientRow(foundPatients[index], index);
      } else if (selectPatient && isKey.enter) {
        this.onEnterPressed(event);
      }
    }

    const tableContainer = document.getElementById('patient-search-table-container');
    const focusedTable = tableContainer ? tableContainer
        .querySelector('.ReactVirtualized__Grid.ReactVirtualized__Table__Grid') : null;

    if (tableContainer && (isKey.pageUp || isKey.pageDown)) {
      tableContainer.focus();
    }

    const activeElement = document.activeElement === tableContainer;

    if (isKey.arrowUp || isKey.pageUp || isKey.pageDown) {
      event.preventDefault();
    }

    if (isKey.pageUp && focusedTable && activeElement) {
      //@ts-ignore
      const height = focusedTable.offsetHeight;

      if (selectedPatientIx !== undefined) {
        const jumpTo = selectedPatientIx - Math.floor(height / 25);

        if (jumpTo < 0) {
          this.onSelectPatientRow(foundPatients[0], 0);
        } else {
          this.onSelectPatientRow(foundPatients[jumpTo], jumpTo);
        }
      }
    }

    if (isKey.pageDown && focusedTable && activeElement) {
      //@ts-ignore
      const height = focusedTable.offsetHeight;

      if (selectedPatientIx !== undefined) {

        const jumpTo = Math.floor(height / 25) + selectedPatientIx;

        if (jumpTo > foundPatients.length - 1) {
          this.onSelectPatientRow(foundPatients[foundPatients.length - 1], foundPatients.length - 1);
        } else {
          this.onSelectPatientRow(foundPatients[jumpTo], jumpTo);
        }
      }
    }

    if (source) {
      const searchElementId = source === 'search' ? "patient-search-input" : "patient-first-name-search-input";
      const searchInputElement = document.getElementById(searchElementId)
      if (searchInputElement) {
        searchInputElement.focus();
      }
    }

  }

  render() {

    const {selectedPatient, patientsLoaded} = this.state;
    const {getMessage, patientSearchOpen} = this.props;

    return (
        <Modal
            open={patientSearchOpen}
            onClose={this.onCloseModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage('patient.search.title')}</Modal.Header>
          <FormModalContentContainer>
            <PraxFormGrid>
              <FinalForm
                  onSubmit={() => this.onPatientChosen(selectedPatient)}
                  initialValues={{
                    searchById: true,
                    searchByExternalIdManual: true,
                    searchByName: true,
                    searchByFirstName: true,
                    searchByCity: true,
                    searchByBirthDate: true
                  }}
                  subscription={{values: true}}
                  render={this.renderSearchForm}
              />
              <Grid.Row>
                <Grid.Column>
                  <TableWrapperDiv>
                    {patientsLoaded
                        ? this.renderPatientsSegment()
                        : <LoaderComponent message={getMessage("patient.loading")}/>}
                  </TableWrapperDiv>
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
            <FormButtonsRow>
              <Button compact primary onClick={() => this.onPatientChosen(selectedPatient)}
                      disabled={selectedPatient === undefined}>
                {getMessage('button.ok')}
              </Button>
              <Button compact onClick={this.onCloseModal}
              >{getMessage('button.cancel')}</Button>
            </FormButtonsRow>
          </FormModalContentContainer>
        </Modal>
    );
  }

  renderSearchForm = ({values, form}: FormRenderProps) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <Grid.Row>
            <ReactHotkeys
                keyName="alt+n,option+n"
                filter={() => true}
            >
              <Grid.Column width={16} verticalAlign="middle">
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={2} verticalAlign="middle">
                      {getMessage('patient.search.label')}:
                    </Grid.Column>
                    <Grid.Column width={14} verticalAlign="middle">
                      <Field id="patient-search-input"
                             name="searchText"
                             component={Input}
                             fluid
                             autoFocus
                             onKeyDown={(e: any) => this.changeSelectedPatient(e, 'search')}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <React.Fragment>
                    <Grid.Row>
                      <Grid.Column width={2}>
                      </Grid.Column>
                      <Grid.Column width={14}>
                        <CheckboxDiv>
                          <Field
                              name="searchById"
                              component={CheckBox}
                              label={getMessage('patient.search.checkbox.id')}
                          />
                          <Field
                              name="searchByName"
                              component={CheckBox}
                              label={getMessage('patient.search.checkbox.name')}
                          />
                          <Field
                              name="searchByCity"
                              component={CheckBox}
                              label={getMessage('patient.search.checkbox.city')}
                          />
                          <Field
                              name="searchByBirthDate"
                              component={CheckBox}
                              label={getMessage('patient.search.table.birthdate')}
                          />
                          <Field
                              name="searchByExternalIdManual"
                              component={CheckBox}
                              label={getMessage('patient.search.checkbox.externalIdManual')}
                          />
                          <Field
                              name="searchAll"
                              component={CheckBox}
                              label={getMessage('patient.search.checkbox.substring')}
                          />
                          <Field
                              name="includeInactive"
                              component={CheckBox}
                              label={getMessage('patient.search.checkbox.inactive')}
                          />
                        </CheckboxDiv>
                      </Grid.Column>
                    </Grid.Row>
                  </React.Fragment>
                </PraxFormGrid>
              </Grid.Column>
            </ReactHotkeys>
          </Grid.Row>
          <FormSpy subscription={{values: true}} onChange={this.searchPatient}/>
        </React.Fragment>
    );
  };

  birthDateCellRenderer = ({cellData}: any) => {
    if (moment(cellData).isValid()) {
      return (
          <div>{moment(cellData).format("DD.MM.YYYY")}</div>
      );
    }
    return "";
  };

  rowGetter = ({index}: any) => {
    const {foundPatients} = this.state;

    return foundPatients[index];
  };

  foundPatientRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {'aria-rowindex': index + 1};
    const {foundPatients, selectedPatient} = this.state;

    let styles = {
      ...style,
      ...rowStyle
    };

    if (selectedPatient && selectedPatient.id == foundPatients[index].id) {
      styles = {
        ...style,
        ...selectedRow
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.markPatient(index)}
            onDoubleClick={() => this.onPatientChosen(foundPatients[index])}
        >
          {columns}
        </div>
    );
  };

  renderPatientsSegment = () => {

    const {foundPatients, selectedPatientIx} = this.state;
    const {getMessage} = this.props;

    return (

        <div id="patient-search-table-container"
             className="patients-search-container"
             style={{height: "400px"}}
             tabIndex={0}
             onKeyDown={(event) => this.changeSelectedPatient(event)}
        >
          <VirtualizedTable
              rowCount={foundPatients.length}
              rowGetter={({index}: any) => foundPatients[index]}
              rowRenderer={this.foundPatientRowRenderer}
              scrollToIndex={selectedPatientIx ? selectedPatientIx : 0}
              columns={[
                {
                  width: 200,
                  label: getMessage('patient.search.table.id'),
                  dataKey: "id"
                },
                {
                  width: 250,
                  label: getMessage('patient.externalIdManual'),
                  dataKey: "externalIdManual"
                },
                {
                  width: 250,
                  label: getMessage('patient.patientExternalId'),
                  dataKey: "externalId"
                },
                {
                  width: 585,
                  label: getMessage('patient.search.table.name'),
                  dataKey: "name"
                },
                {
                  width: 585,
                  label: getMessage('patient.search.table.firstname'),
                  dataKey: "firstName"
                },
                {
                  width: 580,
                  label: getMessage('patient.search.table.city'),
                  dataKey: "city"
                },
                {
                  width: 300,
                  label: getMessage('patient.search.table.birthdate'),
                  dataKey: "birthDate",
                  cellRenderer: this.birthDateCellRenderer
                }
              ]}
          />
        </div>
    )
  };

}

export default withMessages(PatientSearch);
